import type { HexGrid } from '@rw/geo';
import type { RegionDto, TerritoryDto } from '@rw/shared';
import type { Feature, FeatureCollection, MultiLineString, MultiPolygon, Polygon } from 'geojson';

import { MY_COLOR } from '../../lib/colors';
import type { MapTheme } from '../../stores/map-theme';

export const MINE_COLOR = MY_COLOR;
/** Sectors the viewer lost (matches --color-enemy). */
export const LOST_COLOR = '#ff4d6d';

/** Colours of our layers that depend on the base map being light or dark. */
export interface MapPalette {
  /** Everything outside the game area, painted over the base map: players only see their own city. */
  outside: string;
  neutralLine: string;
  /** Opacity of owned cells' fill (light maps need more to read). */
  ownedFill: number;
  selected: string;
  /** Dark edge under the route so the lime line reads on a light map. */
  routeCasing: string | null;
  label: string;
  labelHalo: string;
  seaLabel: string;
}

export const PALETTES: Record<MapTheme, MapPalette> = {
  dark: {
    outside: '#0a0e13',
    neutralLine: 'rgba(255,255,255,0.13)',
    ownedFill: 0.36,
    selected: '#ffffff',
    routeCasing: null,
    label: '#e5e7eb',
    labelHalo: '#0a0e13',
    seaLabel: '#7f8ea3',
  },
  light: {
    outside: '#cfd6df',
    neutralLine: 'rgba(15,23,42,0.16)',
    ownedFill: 0.45,
    selected: '#0a0e13',
    routeCasing: '#14532d',
    label: '#1e293b',
    labelHalo: '#ffffff',
    seaLabel: '#3b5b7a',
  },
};

export type CellState = 'mine' | 'enemy' | 'neutral';

export type MapViewMode = 'runners' | 'clubs';

export function cellsFeatureCollection(
  grid: HexGrid,
  keys: Iterable<string>,
  owners: Map<string, TerritoryDto>,
  myUserId: string | null,
  mode: MapViewMode = 'runners',
  myClubId: string | null = null,
): FeatureCollection<Polygon> {
  const features: FeatureCollection<Polygon>['features'] = [];
  for (const key of keys) {
    const t = owners.get(key);
    let state: CellState;
    let color: string;
    if (!t?.owner) {
      state = 'neutral';
      color = '#ffffff';
    } else if (mode === 'clubs') {
      // Club view: your club in your colour, other clubs in theirs, club-less runners greyed.
      const club = t.owner.club;
      state = club && club.id === myClubId ? 'mine' : 'enemy';
      color = state === 'mine' ? MINE_COLOR : (club?.color ?? '#6b7d91');
    } else {
      state = t.owner.id === myUserId ? 'mine' : 'enemy';
      color = state === 'mine' ? MINE_COLOR : t.owner.color;
    }
    features.push({
      type: 'Feature',
      properties: { key, state, color },
      geometry: { type: 'Polygon', coordinates: [grid.polygonOf(key)] },
    });
  }
  return { type: 'FeatureCollection', features };
}

export function keysFeatureCollection(grid: HexGrid, keys: Iterable<string>, props: Record<string, unknown> = {}) {
  const features: FeatureCollection<Polygon>['features'] = [];
  for (const key of keys) {
    features.push({ type: 'Feature', properties: { key, ...props }, geometry: { type: 'Polygon', coordinates: [grid.polygonOf(key)] } });
  }
  return { type: 'FeatureCollection', features } as FeatureCollection<Polygon>;
}

export function routeFeature(lines: Array<Array<[number, number]>>): FeatureCollection<MultiLineString> {
  return {
    type: 'FeatureCollection',
    features: lines.length
      ? [{ type: 'Feature', properties: {}, geometry: { type: 'MultiLineString', coordinates: lines } }]
      : [],
  };
}

export function circlePolygon(lat: number, lng: number, radiusM: number, steps = 48): FeatureCollection<Polygon> {
  const coords: [number, number][] = [];
  const dLat = radiusM / 111_320;
  const dLng = radiusM / (111_320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    coords.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return { type: 'FeatureCollection', features: [{ type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [coords] } }] };
}

export const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

export function outsideMask(boundary: RegionDto['boundary']): Feature<Polygon> {
  const parts = boundary.type === 'Polygon' ? [boundary.coordinates] : boundary.coordinates;
  const world: [number, number][] = [
    [-180, -85],
    [180, -85],
    [180, 85],
    [-180, 85],
    [-180, -85],
  ];
  // The world with the area's outer rings cut out as holes.
  return { type: 'Feature', properties: {}, geometry: { type: 'Polygon', coordinates: [world, ...parts.map((p) => p[0]!)] } };
}

/** Vietnam's archipelagos, always named on the map as Vietnamese territory. */
export const VN_ARCHIPELAGOS = [
  { name: 'Hoàng Sa Archipelago', province: 'Đà Nẵng', lng: 111.75, lat: 16.45 },
  { name: 'Trường Sa Archipelago', province: 'Khánh Hòa', lng: 114.25, lat: 10.2 },
] as const;

/** Biển Đông, named by us; the base map's own name for this sea (in any language) is hidden. */
export const EAST_SEA = { name: 'East Sea', lng: 113.6, lat: 13.6 } as const;
export const EAST_SEA_BASE_NAMES = ['South China Sea', 'South China sea', 'Biển Đông', 'Bien Dong', '南海', '南中国海', 'Laut China Selatan', 'West Philippine Sea', 'Dagat Timog Tsina'];

/** Sea areas of the archipelagos: base-map labels inside them are hidden so only ours show. */
export const VN_ARCHIPELAGO_AREA: MultiPolygon = {
  type: 'MultiPolygon',
  coordinates: [
    // Hoàng Sa
    [
      [
        [110.8, 15.5],
        [113.2, 15.5],
        [113.2, 17.4],
        [110.8, 17.4],
        [110.8, 15.5],
      ],
    ],
    // Trường Sa: open sea, clear of Palawan and Borneo's coasts.
    [
      [
        [111.5, 7.5],
        [116.0, 7.5],
        [116.0, 12.0],
        [111.5, 12.0],
        [111.5, 7.5],
      ],
    ],
  ],
};

type Filter = unknown[];
const LEGACY_COMPARE = new Set(['==', '!=', '<', '<=', '>', '>=']);
const legacyKey = (k: string) => (k === '$type' ? ['geometry-type'] : k === '$id' ? ['id'] : ['get', k]);

/** Base styles (CARTO) still use legacy filters, which can't be mixed with expressions: convert them. */
export function toExpression(f: unknown): unknown {
  if (!Array.isArray(f)) return f;
  const [op, ...args] = f as Filter;
  if (op === 'all' || op === 'any') return [op, ...args.map(toExpression)];
  if (op === 'none') return ['!', ['any', ...args.map(toExpression)]];
  // Already an expression: legacy filters take a key name, then plain values only.
  if (typeof args[0] !== 'string' || args.slice(1).some(Array.isArray)) return f;
  const key = legacyKey(args[0]);
  if (op === '==' || op === '!=') return [op, key, args[1]];
  // Legacy order comparisons are false for a missing or differently typed value; expressions would throw.
  if (LEGACY_COMPARE.has(op as string)) return ['all', ['==', ['typeof', key], typeof args[1]], [op, key, args[1]]];
  if (op === 'in' || op === '!in') {
    const match = ['match', key, args.slice(1), true, false];
    return op === 'in' ? match : ['!', match];
  }
  if (op === 'has') return ['has', args[0]];
  if (op === '!has') return ['!', ['has', args[0]]];
  return f;
}
